import React, { useMemo, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material'
import { type PersonAddress, UsAddressFields, usAddressIsValid, useGlobalMessageHandler, useRoleAddressTypeLuts } from 'components'
import { getEmptyPersonAddress, getEmptyPersonPhone } from 'helpers'
import { type Building } from 'services'
import { PHONE_TYPES } from 'utils'

interface PositionAddressDialogProps {
  open: boolean
  roleId: number | null
  personId: number | null
  filterBuildings?: (buildings: Building[]) => Building[]
  onClose: () => void
  onSave: (address: PersonAddress) => void
}

const buildDraftAddress = (personId: number): PersonAddress => {
  const address = getEmptyPersonAddress(personId)
  const phone = getEmptyPersonPhone(personId, address.id)
  phone.typeId = PHONE_TYPES.PHONE
  phone.displayOrder = 1
  const fax = getEmptyPersonPhone(personId, address.id)
  fax.typeId = PHONE_TYPES.FAX
  fax.displayOrder = 1
  address.phones = [phone, fax]
  return address
}

export const PositionAddressDialog: React.FC<PositionAddressDialogProps> = ({
  open,
  roleId,
  personId,
  filterBuildings,
  onClose,
  onSave
}) => {
  const { lutValues: addressTypeLut } = useRoleAddressTypeLuts()
  const { showErrorMessage } = useGlobalMessageHandler()
  const [address, setAddress] = useState<PersonAddress>(() => buildDraftAddress(personId ?? 0))
  const [typeId, setTypeId] = useState<number | ''>('')

  const typeOptions = useMemo(
    () => Object.values(addressTypeLut.address_types).filter(t => t.role?.id === roleId),
    [addressTypeLut, roleId]
  )

  const halfFilledPhoneLabel = (phones: PersonAddress['phones']): string | null => {
    for (const p of phones) {
      const hasAreaCode = p.areaCode.trim() !== ''
      const hasNumber = (p.phoneNumber ?? '').trim() !== ''
      if (hasAreaCode !== hasNumber) {
        return p.typeId === PHONE_TYPES.FAX ? 'Fax' : 'Phone'
      }
    }
    return null
  }

  const handleSave = (): void => {
    if (typeId === '') return
    if (!usAddressIsValid(address, showErrorMessage)) return
    const halfFilledLabel = halfFilledPhoneLabel(address.phones)
    if (halfFilledLabel != null) {
      showErrorMessage(`${halfFilledLabel} needs both an area code and a number, or neither.`)
      return
    }
    const phones = address.phones.filter(p => p.areaCode.trim() !== '' || (p.phoneNumber ?? '').trim() !== '')
    onSave({ ...address, typeId: Number(typeId), phones })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>New Address</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel id='position-address-type-label'>Address Type *</InputLabel>
          <Select
            labelId='position-address-type-label'
            label='Address Type *'
            value={typeId}
            onChange={e => { setTypeId(e.target.value === '' ? '' : Number(e.target.value)) }}
          >
            {typeOptions.map(t => (
              <MenuItem key={t.id} value={t.id}>{t.type_desc}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <UsAddressFields
          comAddress={address}
          updateAddress={(updated) => { setAddress(updated as PersonAddress) }}
          resetAddress={() => { setAddress(buildDraftAddress(personId ?? 0)) }}
          disabled={false}
          showPhones
          filterBuildings={filterBuildings}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSave} disabled={typeId === ''}>
          Add Address
        </Button>
      </DialogActions>
    </Dialog>
  )
}
